import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, Receipt,
  ClipboardList, ChevronLeft, ChevronRight,
  LogOut, Shield, Settings,
  BarChart3, ArrowLeftRight, BookOpen, TrendingUp,
  Landmark, Tag, ChevronDown, ChevronUp, Scale, NotebookPen,
  Upload, Clock, Database, UsersRound, RefreshCw, CreditCard, AlertCircle,
  Briefcase, Building2, Wallet
} from 'lucide-react';

function NavItem({ to, icon: Icon, label, collapsed }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150
        ${isActive
          ? 'bg-indigo-50 text-indigo-900 border-l-2 border-indigo-900'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }
        ${collapsed ? 'justify-center px-2' : ''}`
      }
    >
      <Icon size={15} className="flex-shrink-0" />
      {!collapsed && <span className="text-xs">{label}</span>}
    </NavLink>
  );
}

function SectionHeader({ label, icon: Icon, color, collapsed, open, onToggle }) {
  if (collapsed) return null;
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md mb-0.5 group transition-colors ${color}`}
      data-testid={`section-header-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-2">
        <Icon size={13} className="flex-shrink-0 opacity-70" />
        <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      </div>
      {open ? <ChevronUp size={11} className="opacity-50" /> : <ChevronDown size={11} className="opacity-50" />}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState({ accountsUSA: false, accountsIndia: false, admin: false });

  const toggle = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));

  const accountsUSAItems = [
    { to: '/accounts/usa/overview',             icon: BarChart3,       label: 'Overview',            permission: 'accounts_usa' },
    { to: '/accounts/usa/transactions',          icon: ArrowLeftRight,  label: 'Transactions',        permission: 'accounts_usa' },
    { to: '/invoices',                           icon: Receipt,         label: 'Invoices',            permission: 'invoices'     },
    { to: '/accounts/usa/import-transactions',   icon: Upload,          label: 'Import Transactions', permission: 'accounts_usa' },
    { to: '/accounts/usa/expenses',              icon: Wallet,          label: 'Expenses',            permission: 'accounts_usa' },
    { to: '/accounts/usa/pending-review',        icon: Clock,           label: 'Pending Review',      permission: 'accounts_usa' },
    { to: '/accounts/usa/journal-entries',       icon: NotebookPen,     label: 'Journal Entries',     permission: 'accounts_usa' },
    { to: '/accounts/usa/pl-report',             icon: TrendingUp,      label: 'P&L Report',          permission: 'accounts_usa' },
    { to: '/accounts/usa/balance-sheet',         icon: Scale,           label: 'Balance Sheet',       permission: 'accounts_usa' },
    { to: '/accounts/usa/aged-receivables',      icon: AlertCircle,     label: 'Aged Receivables',    permission: 'accounts_usa' },
    { to: '/accounts/usa/chart-of-accounts',     icon: BookOpen,        label: 'Chart of Accounts',   permission: 'accounts_usa' },
    { to: '/accounts/bank-accounts',             icon: CreditCard,      label: 'Bank Accounts',       permission: 'accounts_usa' },
    { to: '/accounts/usa/bank-connections',      icon: Landmark,        label: 'Bank Connections',    permission: 'accounts_usa' },
    { to: '/accounts/usa/plaid-transactions',    icon: ArrowLeftRight,  label: 'Imported Transactions', permission: 'accounts_usa' },
    { to: '/accounts/usa/merchant-rules',        icon: Tag,             label: 'Auto-Categorize',     permission: 'accounts_usa' },
    { to: '/accounts/usa/xero-import',           icon: Database,        label: 'Xero Import',         permission: 'accounts_usa' },
    { to: '/accounts/usa/trial-balance',         icon: Scale,           label: 'Trial Balance',       permission: 'accounts_usa' },
  ].filter(i => !i.permission || hasPermission(i.permission));

  const accountsIndiaItems = [
    { to: '/accounts/india/overview',            icon: BarChart3,       label: 'Overview',            permission: 'accounts_india' },
    { to: '/accounts/india/transactions',        icon: ArrowLeftRight,  label: 'Transactions',        permission: 'accounts_india' },
    { to: '/accounts/india/import-transactions', icon: Upload,          label: 'Import Transactions', permission: 'accounts_india' },
    { to: '/accounts/india/expenses',            icon: Wallet,          label: 'Expenses',            permission: 'accounts_india' },
    { to: '/accounts/india/pending-review',      icon: Clock,           label: 'Pending Review',      permission: 'accounts_india' },
    { to: '/accounts/india/journal-entries',     icon: NotebookPen,     label: 'Journal Entries',     permission: 'accounts_india' },
    { to: '/accounts/india/pl-report',           icon: TrendingUp,      label: 'P&L Report',          permission: 'accounts_india' },
    { to: '/accounts/india/balance-sheet',       icon: Scale,           label: 'Balance Sheet',       permission: 'accounts_india' },
    { to: '/accounts/india/chart-of-accounts',   icon: BookOpen,        label: 'Chart of Accounts',   permission: 'accounts_india' },
    { to: '/accounts/india/merchant-rules',      icon: Tag,             label: 'Auto-Categorize',     permission: 'accounts_india' },
  ].filter(i => !i.permission || hasPermission(i.permission));

  const adminItems = isAdmin() ? [
    { to: '/users',        icon: Users,        label: 'Users'       },
    { to: '/audit-log',    icon: ClipboardList,label: 'Audit Log'   },
    { to: '/system-admin', icon: Settings,     label: 'System Admin'},
  ] : [];

  return (
    <div className="flex h-screen bg-slate-50" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-200 ${collapsed ? 'w-14' : 'w-60'}`}
        style={{ flexShrink: 0 }}
      >
        {/* Logo */}
        <div className={`flex items-center h-13 border-b border-slate-200 px-3 py-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <span className="font-bold text-indigo-900 text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Eyvo Finance Ops
            </span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">

          {/* HOME -- a permission like the rest, not part of being signed in */}
          {hasPermission('dashboard') && <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" collapsed={collapsed} />}

          {/* SUPPLIERS & CLIENTS */}
          {hasPermission('suppliers') && <NavItem to="/suppliers" icon={Briefcase} label="Suppliers" collapsed={collapsed} />}
          {hasPermission('clients') && <NavItem to="/clients"   icon={Building2} label="Clients"   collapsed={collapsed} />}

          {/* XERO MIGRATION -- administrators only, as the API is */}
          {isAdmin() && (
            <NavItem to="/settings/xero-migration" icon={RefreshCw} label="Xero Import" collapsed={collapsed} />
          )}

          {/* ACCOUNTS USA */}
          {accountsUSAItems.length > 0 && (
            <div className="pt-1">
              <SectionHeader label="Accounts — Eyvo USA" icon={BarChart3} color="text-indigo-700 hover:bg-indigo-50"
                collapsed={collapsed} open={openSections.accountsUSA} onToggle={() => toggle('accountsUSA')} />
              {(collapsed || openSections.accountsUSA) && (
                <div className="space-y-0.5 ml-1">
                  {accountsUSAItems.map(item => <NavItem key={item.to} {...item} collapsed={collapsed} />)}
                </div>
              )}
            </div>
          )}

          {/* ACCOUNTS INDIA */}
          {accountsIndiaItems.length > 0 && (
            <div className="pt-1">
              <SectionHeader label="Accounts — Eyvo India" icon={BarChart3} color="text-orange-700 hover:bg-orange-50"
                collapsed={collapsed} open={openSections.accountsIndia} onToggle={() => toggle('accountsIndia')} />
              {(collapsed || openSections.accountsIndia) && (
                <div className="space-y-0.5 ml-1">
                  {accountsIndiaItems.map(item => <NavItem key={item.to} {...item} collapsed={collapsed} />)}
                </div>
              )}
            </div>
          )}

          {/* ADMIN */}
          {adminItems.length > 0 && (
            <div className="pt-1">
              <SectionHeader label="Administration" icon={Shield} color="text-rose-600 hover:bg-rose-50"
                collapsed={collapsed} open={openSections.admin} onToggle={() => toggle('admin')} />
              {(collapsed || openSections.admin) && (
                <div className="space-y-0.5 ml-1">
                  {adminItems.map(item => <NavItem key={item.to} {...item} collapsed={collapsed} />)}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User info */}
        <div className={`border-t border-slate-200 p-2.5 ${collapsed ? 'flex justify-center' : ''}`}>
          {!collapsed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-800 text-xs font-semibold">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">{user?.full_name}</p>
                  <div className="flex items-center gap-1">
                    {user?.role === 'admin' && <Shield size={9} className="text-indigo-500" />}
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </div>
              </div>
              <button onClick={logout}
                className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-slate-400"
                data-testid="logout-button" title="Logout">
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button onClick={logout}
              className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-slate-400"
              data-testid="logout-button-collapsed" title="Logout">
              <LogOut size={14} />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
